import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { extractZipCodes, filterDmaByZipList } from '../../components/Map/helpers';
import {
  DMA_ENDPOINT,
  STATES_ENDPOINT,
  ZIPCODE_BY_COORDINATES_ENDPOINT,
  ZIPCODE_ENDPOINT,
} from '../../constants/endpoints';
import { GEO_TYPE, borderIOHeaders } from '../../constants/geo';

const initialState = {
  loadingForecaster: true,
  reportingZipGeoJson: [],
  forecasterGeoFencingZip: [],
  forecasterGeoFencingStates: [],
  forecasterDMAList: [],
  forecasterGeoFencingDMA: [],
  forecasterZipCodeList: [],
  forecasterGeoTargetingDMA: [],
  forecasterGeoTargetingStates: [],
  forecasterGeoTargetingZip: [],
};

export const geoSlice = createSlice({
  name: 'geoData',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getStateByNameGeoData.pending, state => {
        state.loadingForecaster = true;
      })
      .addCase(getStateByNameGeoData.rejected, state => {
        state.loadingForecaster = false;
      })
      .addCase(getStateByNameGeoData.fulfilled, (state, action) => {
        const { geoJsonData, type } = action.payload;
        state.loadingForecaster = false;
        if (type === GEO_TYPE.GEO_FENCING) {
          state.forecasterGeoFencingStates = geoJsonData;
        } else {
          state.forecasterGeoTargetingStates = geoJsonData;
        }
      })
      .addCase(getZipByCoordinates.pending, state => {
        state.forecasterGeoFencingDMA = [];
        state.forecasterGeoFencingStates = [];
        state.forecasterGeoFencingZip = [];
        state.loadingForecaster = true;
      })
      .addCase(getZipByCoordinates.rejected, state => {
        state.loadingForecaster = false;
      })
      .addCase(getZipByCoordinates.fulfilled, (state, action) => {
        state.loadingForecaster = false;
        state.forecasterGeoFencingZip = action.payload;
        state.forecasterZipCodeList = extractZipCodes(action.payload);
      })
      .addCase(getDMAListByStates.pending, state => {
        state.loadingForecaster = true;
      })
      .addCase(getDMAListByStates.rejected, state => {
        state.loadingForecaster = false;
      })
      .addCase(getDMAListByStates.fulfilled, (state, action) => {
        state.loadingForecaster = false;
        state.forecasterDMAList = action.payload;
      })
      .addCase(getDMAByNameGeoData.pending, state => {
        state.loadingForecaster = true;
      })
      .addCase(getDMAByNameGeoData.rejected, state => {
        state.loadingForecaster = false;
      })
      .addCase(getDMAByNameGeoData.fulfilled, (state, action) => {
        const { geoJsonData, type } = action.payload;
        state.loadingForecaster = false;
        if (type === GEO_TYPE.GEO_FENCING) {
          state.forecasterGeoFencingDMA = geoJsonData;
        } else {
          state.forecasterGeoTargetingDMA = geoJsonData;
        }
      })
      .addCase(getZipByCodesGeoData.pending, state => {
        state.loadingForecaster = true;
      })
      .addCase(getZipByCodesGeoData.rejected, state => {
        state.loadingForecaster = false;
      })
      .addCase(getZipByCodesGeoData.fulfilled, (state, action) => {
        const { geoJsonData, type } = action.payload;
        state.loadingForecaster = false;
        if (type === GEO_TYPE.GEO_TARGETING) {
          state.forecasterGeoTargetingZip = geoJsonData;
        } else {
          state.reportingZipGeoJson = geoJsonData;
        }
      });
  },
});

export const { setSelectedUserId } = geoSlice.actions;

export const getStateByNameGeoData = createAsyncThunk('geoSlice/getStateByNameGeoData', async payload => {
  try {
    const { stateCodes, type } = payload;
    if (!stateCodes || stateCodes.length === 0) {
      return;
    }
    const allFeatures = [];

    for (const stateCode of stateCodes) {
      const responseResult = await axios.get(`${STATES_ENDPOINT}${stateCode}`, { headers: borderIOHeaders });
      for (const feature of responseResult.data.features) {
        feature.properties.type = 'State';
        allFeatures.push(feature);
      }
    }

    const geoJsonData = {
      type: 'FeatureCollection',
      features: allFeatures,
    };

    payload?.onSuccess?.();
    return { geoJsonData, type };
  } catch (err) {
    console.error(err);
    throw err?.response?.data;
  }
});

export const getDMAListByStates = createAsyncThunk('geoSlice/getDMAListByStates', async payload => {
  try {
    const { stateCodes } = payload;
    if (!stateCodes || stateCodes.length === 0) {
      return;
    }
    const dmaSet = new Set();

    for (const stateCode of stateCodes) {
      const responseResult = await axios.get(`${DMA_ENDPOINT}/state/${stateCode}`, {
        headers: borderIOHeaders,
      });
      responseResult.data.forEach(dma => dmaSet.add(dma));
    }

    const dmaList = Array.from(dmaSet);
    payload?.onSuccess?.();
    return dmaList;
  } catch (err) {
    console.error(err);
    throw err?.response?.data;
  }
});

export const getDMAByNameGeoData = createAsyncThunk('geoSlice/getDMAByNameGeoData', async (payload, { getState }) => {
  try {
    const dmaNames = payload.forecasterDMAList;
    const { type } = payload;
    if (!dmaNames || dmaNames.length === 0) {
      return;
    }
    const {
      geoData: { forecasterZipCodeList },
    } = getState();
    const allFeatures = [];
    const processedFeatures = [];
    let featuresForProcessing = [];

    for (const name of dmaNames) {
      const responseResult = await axios.get(DMA_ENDPOINT, {
        params: {
          name,
        },
        headers: borderIOHeaders,
      });
      allFeatures.push(...responseResult.data.features);
    }

    if (type === GEO_TYPE.GEO_FENCING) {
      featuresForProcessing = filterDmaByZipList(allFeatures, forecasterZipCodeList);
    } else {
      featuresForProcessing = allFeatures;
    }

    for (const feature of featuresForProcessing) {
      feature.properties.type = 'DMA';
      processedFeatures.push(feature);
    }

    const geoJsonData = {
      type: 'FeatureCollection',
      features: processedFeatures,
    };

    payload?.onSuccess?.();
    return {
      type,
      geoJsonData,
    };
  } catch (err) {
    console.error(err);
    throw err?.response?.data;
  }
});

// see src/store/slices/reportingDashboard/beeswaxSlice.js
export const getZipByCodesGeoData = createAsyncThunk('geoSlice/getZipByCodesGeoData', async payload => {
  try {
    const { zipCodes, type } = payload;
    const responseResult = await axios.get(`${ZIPCODE_ENDPOINT}?zipcode=${zipCodes}`, {
      headers: borderIOHeaders,
    });

    payload?.onSuccess?.();
    return {
      type,
      geoJsonData: responseResult.data,
    };
  } catch (err) {
    console.error(err);
    throw err?.response?.data;
  }
});

export const getZipByCoordinates = createAsyncThunk('geoSlice/getZipByCoordinates', async payload => {
  try {
    const { coordinates, radius } = payload;

    const responseResult = await axios.get(
      `${ZIPCODE_BY_COORDINATES_ENDPOINT}/location?latitude=${coordinates[1]}&longitude=${coordinates[0]}&radius=${radius}&showCirlce=false&showDetails=false`,
      { headers: borderIOHeaders },
    );
    payload?.onSuccess?.();

    return responseResult.data;
  } catch (err) {
    console.error(err);
    throw err?.response?.data;
  }
});

export default geoSlice.reducer;
